/**
 * Quick-add a contractor who is not in the system yet, from a phase owner field.
 * The contractor is usable immediately but carries "documents pending" until the
 * COI, W-9 and licence are uploaded — a super admin can override that on the spot.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, UserPlus, ShieldAlert } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { API, useAuth } from "@/App";

const BLANK = {
  company_name: "", name: "", email: "", phone: "", trade: "", license_number: "", notes: "",
};

export const ContractorQuickAddDialog = ({ open, onClose, onCreated }) => {
  const { token, user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [form, setForm] = useState(BLANK);
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (form.company_name.trim().length < 2) return toast.error("Company name is required");
    if (override && reason.trim().length < 5) return toast.error("Record why you are overriding the documents");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/project-command/contractors/quick-add`, {
        ...form,
        compliance_override: override,
        override_reason: reason,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${r.data.contractor.company_name} added`, {
        description: override
          ? "Documents overridden — recorded against your account"
          : "Documents pending: COI, W-9 and trade licence",
      });
      setForm(BLANK); setOverride(false); setReason("");
      onCreated?.(r.data.contractor);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add that contractor");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="contractor-quick-add-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-orange-500" /> Add a new contractor
          </DialogTitle>
          <DialogDescription>
            They become assignable straight away. Compliance documents are still required.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Company name *</Label>
            <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
              placeholder="e.g. Gulf Coast Mechanical LLC" data-testid="qa-contractor-company" />
          </div>
          <div>
            <Label className="text-xs">Contact name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)}
              data-testid="qa-contractor-contact" />
          </div>
          <div>
            <Label className="text-xs">Trade</Label>
            <Input value={form.trade} onChange={(e) => set("trade", e.target.value)}
              placeholder="plumbing, electrical…" data-testid="qa-contractor-trade" />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input value={form.email} onChange={(e) => set("email", e.target.value)}
              data-testid="qa-contractor-email" />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)}
              data-testid="qa-contractor-phone" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Licence number</Label>
            <Input value={form.license_number} onChange={(e) => set("license_number", e.target.value)}
              data-testid="qa-contractor-license" />
          </div>
        </div>

        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900">
          <p className="font-bold flex items-center gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5" /> Documents still required
          </p>
          <p className="mt-1">Certificate of insurance · W-9 · trade licence. They stay flagged
            <strong> documents pending</strong> on every phase until those are uploaded.</p>
          {isSuper ? (
            <label className="mt-2 flex items-start gap-2 cursor-pointer"
              data-testid="qa-contractor-override-label">
              <input type="checkbox" checked={override} className="mt-0.5"
                onChange={(e) => setOverride(e.target.checked)}
                data-testid="qa-contractor-override" />
              <span className="font-semibold">Override the document requirement (super admin)</span>
            </label>
          ) : (
            <p className="mt-2 italic">Only a super admin can override this requirement.</p>
          )}
          {override && (
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              className="mt-2 bg-white text-[12px]"
              placeholder="Why is this being overridden? Recorded against your account."
              data-testid="qa-contractor-override-reason" />
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="qa-contractor-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700"
            data-testid="qa-contractor-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <UserPlus className="w-4 h-4 mr-1.5" />}
            Add contractor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContractorQuickAddDialog;
